<template>
  <!--
    Standardized location picker. v-model is the persisted location TEXT (string).
    Suggests from `locations` (the saved Portfolio locations); still allows a
    free-text one-off. Mirrors the original PortfolioView image-location picker.
  -->
  <div class="loc-picker">
    <input
      :value="modelValue"
      :placeholder="placeholder"
      @input="onInput(($event.target as HTMLInputElement).value)"
      @focus="open = true; search()"
      @blur="blur"
    />
    <ul v-if="open && results.length" class="contrib-suggest">
      <li v-for="l in results" :key="l.id" @mousedown.prevent="pick(l)">
        <strong>{{ locLabel(l) }}</strong>
        <span class="contrib-meta" v-if="l.location_address">{{ l.location_address }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface SavedLocation {
  id: number
  location_venue?: string | null
  location_address?: string | null
  location_city?: string | null
  location_state?: string | null
}

const props = withDefaults(defineProps<{
  modelValue: string
  locations: SavedLocation[]
  placeholder?: string
}>(), { placeholder: 'Type to search saved locations…' })

const emit = defineEmits<{ (e: 'update:modelValue', v: string): void }>()

const open = ref(false)
const results = ref<SavedLocation[]>([])

function locLabel(l: SavedLocation): string {
  return [l.location_venue, l.location_city, l.location_state].filter(Boolean).join(' · ')
    || l.location_address || `#${l.id}`
}

function search() {
  const q = (props.modelValue || '').trim().toLowerCase()
  const all = props.locations || []
  results.value = q
    ? all.filter(l => locLabel(l).toLowerCase().includes(q) || (l.location_address || '').toLowerCase().includes(q))
    : all.slice(0, 12)
}
function onInput(v: string) {
  emit('update:modelValue', v)
  open.value = true
  // Re-run against the new value on next tick via the emitted model; filter locally now too.
  const q = v.trim().toLowerCase()
  const all = props.locations || []
  results.value = q
    ? all.filter(l => locLabel(l).toLowerCase().includes(q) || (l.location_address || '').toLowerCase().includes(q))
    : all.slice(0, 12)
}
function pick(l: SavedLocation) {
  emit('update:modelValue', locLabel(l))
  results.value = []
  open.value = false
}
function blur() { setTimeout(() => { open.value = false }, 150) }
</script>

<style scoped>
.loc-picker { position: relative; }
.loc-picker input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-input);
  color: var(--text-primary);
  font-size: 0.875rem;
}
.loc-picker input:focus { outline: none; border-color: var(--border-focus); }
.contrib-suggest {
  position: absolute;
  z-index: 20;
  top: calc(100% + 2px);
  left: 0; right: 0;
  margin: 0; padding: 0.25rem 0;
  list-style: none;
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  max-height: 240px;
  overflow-y: auto;
  box-shadow: var(--shadow-md, 0 4px 12px rgba(0,0,0,0.25));
}
.contrib-suggest li {
  padding: 0.4rem 0.7rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.contrib-suggest li:hover { background: var(--bg-surface-hover); }
.contrib-suggest strong { color: var(--text-primary); font-size: 0.85rem; }
.contrib-meta { color: var(--text-muted); font-size: 0.72rem; }
</style>
